import React from 'react';
import { useLocation, useNavigate } from 'react-router';

import PageBaseProps from 'types/PageBase';
import { ErrorTypeEnum, isApiErrorResponse } from 'types/Error';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

import { useDispatch, useSelector } from 'hooks';
import { setNotFound } from 'modules/auth/actions';
import { isFlowTypeModalVisible } from 'modules/selfServices/selectors';
import {
  getAllMFAConnectionMethods,
  hasPhoneNumberLogin,
  isBreakdownAvailable,
  isCodeLoginEnabled
} from 'modules/dealers/selectors';

import { FlowTypeModal } from 'components/ui';
import useQrCodeScanner from 'hooks/useQrCodeScanner';

import LoginCardLayout from './LoginCardLayout';
import LoginFormLayout from './LoginFormLayout';
import { getLoginData, getNotFoundSelfService } from 'modules/auth/selectors';
import BreakdownRedirectionModal from './BreakdownRedirectionModal';
import { formatPhoneNumber } from 'modules/communication/utils';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';

const Login: React.FC<PageBaseProps> = ({ onNext, onPrev }) => {
  useQrCodeScanner(false, onNext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, state: pageState } = useLocation();

  const loginData = useSelector(getLoginData);
  const qrCodeAvailable = useSelector(isCodeLoginEnabled);
  const breakdownAvailable = useSelector(isBreakdownAvailable);
  const isFlowTypeModalOpen = useSelector(isFlowTypeModalVisible);
  const canUsePhoneNumberLogin = useSelector(hasPhoneNumberLogin);
  const isNotFoundSelfService = useSelector(getNotFoundSelfService);
  const MFAConnectionMethods = useSelector(getAllMFAConnectionMethods);

  const { registration = '', phoneNumber } = loginData;
  const isCheckin = pageState?.type === SelfServiceType.CHECK_IN;
  const isBreakdown = pageState?.type === SelfServiceType.BREAKDOWN;
  const utilizeAlternativeLoginLayout = (canUsePhoneNumberLogin || qrCodeAvailable) && !isBreakdown;
  const showRedirectionModal = isCheckin && breakdownAvailable && isNotFoundSelfService;

  const handleHideRedirectionModal = React.useCallback(() => dispatch(setNotFound(false)), [dispatch]);

  const handleOnLoginSuccess = React.useCallback((loginMethod: SelfOfficeLoginMethod) => {
    if (MFAConnectionMethods.includes(loginMethod)) {
      onNext?.();
    }
  }, [MFAConnectionMethods, onNext]);

  const handleOnLoginError = React.useCallback((error: unknown) => {
    if (isApiErrorResponse(error)) {
      if (error.data.errorType === ErrorTypeEnum.MFA_CODE_TIMEOUT) {
        onNext?.();
      } else if (error.data.errorType === ErrorTypeEnum.SO_WRONG_KIOSK) {
        navigate(`/wrongKiosk${search}`);
      }
    }
  }, [navigate, onNext, search]);

  return (
    <>
      {utilizeAlternativeLoginLayout && (
        <LoginCardLayout onLoginSuccess={handleOnLoginSuccess} onLoginError={handleOnLoginError} />
      )}
      {!utilizeAlternativeLoginLayout && (
        <LoginFormLayout onLoginSuccess={handleOnLoginSuccess} onPrev={onPrev} onLoginError={handleOnLoginError}/>
      )}
      {isFlowTypeModalOpen && <FlowTypeModal />}
      {showRedirectionModal && (
        <BreakdownRedirectionModal
          plateNumber={registration}
          phoneNumber={formatPhoneNumber(phoneNumber)}
          onCancel={handleHideRedirectionModal} />
      )}
    </>
  );
};

export default Login;
